import { useEffect, useState } from "react"
import Link from "@/src/components/Link"
import { useLocation, useNavigate } from "react-router-dom"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { LogOut, Menu, X, Shield, ChevronDown, ChevronRight } from "lucide-react"
import { toast } from "sonner"
import { adminNavigation } from "./admin-navigation"
import { Badge } from "@/components/ui/badge"
import { useSlipStatistics } from "@/src/hooks/usePaymentSlips"

interface AdminSidebarProps {
  className?: string
}

export function AdminSidebar({ className }: AdminSidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const pathname = location.pathname
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set())
  const { data: slipStats } = useSlipStatistics()

  const pendingSlipCount = slipStats?.summary?.pendingCount ?? 0
  const getMenuBadgeCount = (href: string) => {
    if (href === "/admin/payment-slips" && pendingSlipCount > 0) {
      return pendingSlipCount
    }
    return null
  }

  const toggleGroup = (href: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev)
      if (next.has(href)) next.delete(href)
      else next.add(href)
      return next
    })
  }

  // Get user data from localStorage
  const getUserData = () => {
    const userDataStr = localStorage.getItem('userData')
    if (userDataStr) {
      try {
        return JSON.parse(userDataStr)
      } catch {
        return null
      }
    }
    return null
  }

  const userData = getUserData()

  // Check access permissions
  const checkAccess = (item: any) => {
    if (!userData?.permissions) return false
    
    // Check required permissions (OR logic)
    if (item.requiredPermissions) {
      return item.requiredPermissions.some((permission: string) => 
        userData.permissions.includes(permission)
      )
    }
    
    // Check required permissions (AND logic)
    if (item.requiredPermissionsAll) {
      return item.requiredPermissionsAll.every((permission: string) => 
        userData.permissions.includes(permission)
      )
    }

    return true
  }

  // Filter navigation based on permissions
  const filteredNavigation = adminNavigation.map(section => ({
    ...section,
    items: section.items.filter(item => checkAccess(item))
  })).filter(section => section.items.length > 0)

  const handleLogout = () => {
    localStorage.clear()
    toast.success('ออกจากระบบสำเร็จ')
    navigate('/admin/login')
  }

  useEffect(() => {
    setIsMobileOpen(false)
  }, [pathname])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.getElementById("admin-mobile-sidebar")
      const menuButton = document.getElementById("admin-mobile-menu-button")

      if (
        isMobileOpen &&
        sidebar &&
        !sidebar.contains(event.target as Node) &&
        menuButton &&
        !menuButton.contains(event.target as Node)
      ) {
        setIsMobileOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isMobileOpen])

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 lg:p-6 lg:px-3.5 lg:py-3">
        <div className="flex items-center justify-center mb-4">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Admin Panel</h2>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-2 lg:py-4">
        {filteredNavigation.map((section) => (
          <div key={section.title} className="mb-4 lg:mb-6">
            <h3 className="px-4 lg:px-6 text-xs lg:text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2 lg:mb-3">
              {section.title}
            </h3>
            <nav className="space-y-1 px-2 lg:px-3">
              {section.items.map((item) => {
                const isActive = pathname === item.href
                const IconComponent = item.icon
                const hasChildren = !!(item.children && item.children.length > 0)
                const hasActiveChild = item.children?.some(c => pathname.startsWith(c.href)) ?? false
                const isGroupOpen = openGroups.has(item.href) || hasActiveChild
                const badgeCount = getMenuBadgeCount(item.href)

                if (hasChildren) {
                  return (
                    <div key={item.name}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(item.href)}
                        className={cn(
                          "flex items-center px-2 lg:px-3 py-2 text-xs lg:text-sm font-medium rounded-lg transition-colors w-full cursor-pointer",
                          (isActive || hasActiveChild)
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-700 hover:bg-gray-100 hover:text-gray-900",
                        )}
                      >
                        <IconComponent className="mr-2 lg:mr-3 h-4 w-4 lg:h-5 lg:w-5 flex-shrink-0" />
                        <span className="flex-1 truncate text-left">{item.name}</span>
                        {badgeCount !== null && (
                          <Badge
                            className="ml-2 min-w-5 h-5 px-1.5 text-[10px] leading-none bg-red-600 text-white border-red-600"
                          >
                            {badgeCount}
                          </Badge>
                        )}
                        {isGroupOpen
                          ? <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 ml-1" />
                          : <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 ml-1" />}
                      </button>
                      {isGroupOpen && (
                        <div className="ml-3 mt-0.5 space-y-0.5 border-l-2 border-gray-100 pl-2">
                          {item.children!.filter(c => checkAccess(c)).map(child => {
                            const isChildActive = pathname === child.href
                            const ChildIcon = child.icon
                            return (
                              <Link
                                key={child.name}
                                href={child.href}
                                className="flex items-center w-full"
                                onClick={() => setIsMobileOpen(false)}
                              >
                                <div
                                  className={cn(
                                    "flex items-center px-2 py-1.5 text-xs font-medium rounded-md transition-colors w-full",
                                    isChildActive
                                      ? "bg-blue-600 text-white cursor-pointer"
                                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 cursor-pointer",
                                  )}
                                >
                                  <ChildIcon className="mr-2 h-3.5 w-3.5 flex-shrink-0" />
                                  <span className="truncate">{child.name}</span>
                                </div>
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                }

                return (
                  <div key={item.name}>
                    <Link
                      href={item.href}
                      className="flex items-center w-full"
                      onClick={() => setIsMobileOpen(false)}
                    >
                      <div
                        className={cn(
                          "flex items-center px-2 lg:px-3 py-2 text-xs lg:text-sm font-medium rounded-lg transition-colors w-full",
                          isActive 
                            ? "bg-blue-600 text-white cursor-pointer" 
                            : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 cursor-pointer",
                        )}
                      >
                        <IconComponent className="mr-2 lg:mr-3 h-4 w-4 lg:h-5 lg:w-5 flex-shrink-0" />
                        <span className="truncate">{item.name}</span>
                        {badgeCount !== null && (
                          <Badge
                            className="ml-auto min-w-5 h-5 px-1.5 text-[10px] leading-none bg-red-600 text-white border-red-600"
                          >
                            {badgeCount}
                          </Badge>
                        )}
                      </div>
                    </Link>
                  </div>
                )
              })}
            </nav>
          </div>
        ))}
      </div>

      {/* Admin Info & Permissions */}
      <div className="p-3 lg:p-4 border-t border-gray-200">
        <div className="rounded-lg p-3 lg:p-4 bg-background border lg:px-3 lg:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 lg:space-x-3 min-w-0">
              <div className="w-6 h-6 lg:w-8 lg:h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="w-3 h-3 lg:w-4 lg:h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs lg:text-sm font-medium text-gray-900 truncate">
                  {userData?.businessName || 'Admin User'}
                </p>
                <p className="text-xs text-gray-500">
                  สิทธิ์: {userData?.permissions?.length || 0} permissions
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-gray-500 hover:text-gray-700 p-1 lg:p-2" 
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 lg:h-5 lg:w-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <>
      {/* Mobile Menu Button */}
      <Button
        id="admin-mobile-menu-button"
        variant="outline"
        size="sm"
        className="lg:hidden fixed top-4 left-4 z-50 bg-white shadow-lg"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
      >
        {isMobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
      </Button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30" />
      )}

      {/* Mobile sidebar */}
      <div
        id="admin-mobile-sidebar"
        className={cn(
          "lg:hidden fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out",
          isMobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <SidebarContent />
      </div>

      {/* Desktop sidebar */}
      <div
        className={cn(
          "hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white border-r border-gray-200",
          className,
        )}
      >
        <SidebarContent />
      </div>
    </>
  )
}